import React, { useState, useEffect } from "react";
import { useTranslation } from "../context/TranslationContext";

const Countdown = ({ expiredOn, currentDate, className = "" }) => {
  const {
    timeAlmostUp,
    paymentTime,
    timeLeftToComplete,
    dayShort,
    hoursLabel,
    minutesLabel,
    secondsLabel,
  } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isLowTime, setIsLowTime] = useState(false);

  useEffect(() => {
    const expiryDate = new Date(expiredOn);

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = expiryDate - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      // Son 5 dakika kala uyarı modu
      if (difference < 5 * 60 * 1000) {
        setIsLowTime(true);
      } else {
        setIsLowTime(false);
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const t = calculateTimeLeft();
      setTimeLeft(t);
      if (t.days === 0 && t.hours === 0 && t.minutes === 0 && t.seconds === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiredOn]);

  const formatNumber = (num) => (num < 10 ? `0${num}` : num);

  return (
    <>
      <style>{`
        @keyframes swing {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(15deg); }
          50% { transform: rotate(0deg); }
          75% { transform: rotate(-15deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes popIn {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes pulseBorder {
          0% { box-shadow: 0 0 0 0 rgba(234, 88, 12, 0.4); }
          70% { box-shadow: 0 0 0 6px rgba(234, 88, 12, 0); }
          100% { box-shadow: 0 0 0 0 rgba(234, 88, 12, 0); }
        }
        @keyframes gradientFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .icon-swing {
          animation: swing 2s ease-in-out infinite;
          transform-origin: top center;
        }
        .num-pop {
          animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        .border-pulse {
          animation: pulseBorder 2s infinite;
        }
        .gradient-bg {
          background: linear-gradient(-45deg, #fff7ed, #ffedd5, #fee2e2);
          background-size: 200% 200%;
          animation: gradientFlow 5s ease infinite;
        }
      `}</style>

      <div
        className={`flex flex-col md:flex-row items-center justify-between w-full p-5 mb-6 border rounded-2xl shadow-md overflow-hidden relative ${
          isLowTime ? "border-red-200" : "border-orange-200"
        } gradient-bg ${className}`}
      >
        {/* Dekoratif Arkaplan Deseni */}
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-orange-300 rounded-full opacity-10 blur-xl"></div>
        <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-24 h-24 bg-red-300 rounded-full opacity-10 blur-xl"></div>

        <div className="flex items-center gap-4 mb-6 md:mb-0 relative z-10">
          <div
            className={`p-3 rounded-xl shadow-sm ${
              isLowTime
                ? "bg-red-100 text-red-600"
                : "bg-orange-100 text-orange-600"
            }`}
          >
            <svg
              className={`w-7 h-7 icon-swing`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <div className="flex flex-col">
            <span
              className={`font-extrabold text-lg md:text-xl tracking-tight ${
                isLowTime ? "text-red-800" : "text-orange-900"
              }`}
            >
              {isLowTime ? timeAlmostUp : paymentTime}
            </span>
            <span
              className={`text-xs font-medium ${
                isLowTime ? "text-red-600" : "text-orange-600/80"
              }`}
            >
              {timeLeftToComplete}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 relative z-10">
          {timeLeft.days > 0 && (
            <>
              <div className="flex flex-col items-center group">
                <div className="relative bg-white/80 backdrop-blur-sm px-4 py-3 rounded-xl border border-orange-100 shadow-sm min-w-[70px] flex justify-center transition-transform hover:-translate-y-1">
                  <span
                    key={timeLeft.days}
                    className="text-3xl font-black text-orange-600 font-mono leading-none num-pop"
                  >
                    {formatNumber(timeLeft.days)}
                  </span>
                  <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-black/5"></div>
                </div>
                <span className="text-[11px] font-bold text-orange-400 uppercase tracking-widest mt-2">
                  {dayShort}
                </span>
              </div>
              <span className="text-orange-300 font-black text-2xl pb-6 animate-pulse">
                :
              </span>
            </>
          )}

          <div className="flex flex-col items-center group">
            <div className="relative bg-white/80 backdrop-blur-sm px-4 py-3 rounded-xl border border-orange-100 shadow-sm min-w-[70px] flex justify-center transition-transform hover:-translate-y-1">
              <span
                key={timeLeft.hours}
                className="text-3xl font-black text-orange-600 font-mono leading-none num-pop"
              >
                {formatNumber(timeLeft.hours)}
              </span>
              <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-black/5"></div>
            </div>
            <span className="text-[11px] font-bold text-orange-400 uppercase tracking-widest mt-2">
              {hoursLabel}
            </span>
          </div>
          <span className="text-orange-300 font-black text-2xl pb-6 animate-pulse">
            :
          </span>

          <div className="flex flex-col items-center group">
            <div className="relative bg-white/80 backdrop-blur-sm px-4 py-3 rounded-xl border border-orange-100 shadow-sm min-w-[70px] flex justify-center transition-transform hover:-translate-y-1">
              <span
                key={timeLeft.minutes}
                className="text-3xl font-black text-orange-600 font-mono leading-none num-pop"
              >
                {formatNumber(timeLeft.minutes)}
              </span>
              <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-black/5"></div>
            </div>
            <span className="text-[11px] font-bold text-orange-400 uppercase tracking-widest mt-2">
              {minutesLabel}
            </span>
          </div>
          <span className="text-orange-300 font-black text-2xl pb-6 animate-pulse">
            :
          </span>

          <div className="flex flex-col items-center group">
            <div
              className={`relative bg-white/90 backdrop-blur-sm px-4 py-3 rounded-xl border shadow-sm min-w-[70px] flex justify-center transition-transform hover:-translate-y-1 ${
                isLowTime ? "border-red-200 border-pulse" : "border-orange-100"
              }`}
            >
              <span
                key={timeLeft.seconds}
                className={`text-3xl font-black font-mono leading-none num-pop ${
                  isLowTime ? "text-red-600" : "text-orange-600"
                }`}
              >
                {formatNumber(timeLeft.seconds)}
              </span>
              <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-black/5"></div>
            </div>
            <span
              className={`text-[11px] font-bold uppercase tracking-widest mt-2 ${
                isLowTime ? "text-red-400" : "text-orange-400"
              }`}
            >
              {secondsLabel}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Countdown;
